/**
 * @component group-settings-members
 * @css group-settings-members.css
 */
import { escapeHtml } from '../utils/html-utils.js';
import { ADMIN_ROLES, MOD_ROLES } from '../utils/group-invite-policy.js';

const POLICY_OPTIONS = [
  { value: 'members_allowed', label: 'Members' },
  { value: 'mods_and_admins', label: 'Moderators / admins only' },
  { value: 'admins_only', label: 'Admins only' }
];

const ASSIGNABLE_ROLES = ['member', 'moderator', 'admin'];

class GroupSettingsMembers extends HTMLElement {
  constructor() {
    super();
    this.groupId = null;
    this.group = null;
    this.membership = null;
    this.members = [];
    this.joinRequests = [];
    this.loading = true;
    this.error = null;
    this.savingPolicy = false;
  }

  setContext({ groupId, group, membership, currentUser }) {
    this.groupId = groupId;
    this.group = group;
    this.membership = membership;
    this.currentUser = currentUser;
    this.load();
  }

  isMod() {
    const role = this.membership?.role;
    return role && MOD_ROLES.includes(role);
  }

  isAdmin() {
    const role = this.membership?.role;
    return role && ADMIN_ROLES.includes(role);
  }

  async load() {
    if (!window.API || !this.groupId) {
      return;
    }
    this.loading = true;
    this.error = null;
    this.render();
    try {
      const membersData = await window.API.request(`/groups/${this.groupId}/members`);
      this.members = membersData.members || [];
      if (this.isMod()) {
        const jrData = await window.API.request(`/groups/${this.groupId}/join-requests`);
        this.joinRequests = jrData.join_requests || [];
      } else {
        this.joinRequests = [];
      }
    } catch (e) {
      this.error = e.message || 'Could not load members';
      this.members = [];
      this.joinRequests = [];
    } finally {
      this.loading = false;
      this.render();
      this.attachListeners();
    }
  }

  formatDate(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return iso;
    }
  }

  renderInvitePolicySection() {
    if (!this.isAdmin()) {
      return '';
    }
    const inviteCurrent = this.group?.invite_policy || 'members_allowed';
    const eventCurrent = this.group?.event_create_policy || 'members_allowed';
    const rssCurrent = this.group?.rss_add_policy || 'members_allowed';
    const inviteOptions = POLICY_OPTIONS.map((o) => {
      const selected = o.value === inviteCurrent ? 'selected' : '';
      return `<option value="${o.value}" ${selected}>${escapeHtml(o.label)}</option>`;
    }).join('');
    const eventOptions = POLICY_OPTIONS.map((o) => {
      const selected = o.value === eventCurrent ? 'selected' : '';
      return `<option value="${o.value}" ${selected}>${escapeHtml(o.label)}</option>`;
    }).join('');
    const rssOptions = POLICY_OPTIONS.map((o) => {
      const selected = o.value === rssCurrent ? 'selected' : '';
      return `<option value="${o.value}" ${selected}>${escapeHtml(o.label)}</option>`;
    }).join('');
    return `
      <section class="settings-group">
        <div class="group-header">
          <h3>Group policies</h3>
          <p class="group-description">Control who can invite members, create group events, and add RSS feeds.</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="invitePolicySelect">Who can invite</label>
          <p class="form-text">Who can invite new members to this group.</p>
          <select id="invitePolicySelect" class="form-control" ${this.savingPolicy ? 'disabled' : ''}>
            ${inviteOptions}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label" for="eventCreatePolicySelect">Who can create events</label>
          <p class="form-text">Who can create events linked to this group from the group home.</p>
          <select id="eventCreatePolicySelect" class="form-control" ${this.savingPolicy ? 'disabled' : ''}>
            ${eventOptions}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label" for="rssAddPolicySelect">Who can add RSS feeds</label>
          <p class="form-text">Who can subscribe to external feeds that import into this group.</p>
          <select id="rssAddPolicySelect" class="form-control" ${this.savingPolicy ? 'disabled' : ''}>
            ${rssOptions}
          </select>
        </div>
        <div class="settings-panel-actions">
          <button type="button" class="btn btn-primary" id="saveInvitePolicyBtn" ${this.savingPolicy ? 'disabled' : ''}>
            ${this.savingPolicy ? 'Saving…' : 'Save policies'}
          </button>
        </div>
      </section>
    `;
  }

  renderJoinRequests() {
    if (!this.isMod()) {
      return '';
    }
    const rows = this.joinRequests.length
      ? `<div class="member-roster-list">${this.joinRequests.map((jr) => `
          <div class="join-request-row" data-request-id="${jr.id}">
            <div class="member-info">
              <span class="member-username">${escapeHtml(jr.username || `User #${jr.requester_user_id}`)}</span>
              <span class="member-meta">Requested ${this.formatDate(jr.created_at)}</span>
            </div>
            <div class="member-actions">
              <button type="button" class="btn btn-primary btn-sm approve-btn" data-request-id="${jr.id}">Approve</button>
              <button type="button" class="btn btn-secondary btn-sm deny-btn" data-request-id="${jr.id}">Deny</button>
            </div>
          </div>
        `).join('')}</div>`
      : '<p class="empty-hint">No pending join requests.</p>';

    return `
      <section class="settings-group">
        <div class="group-header">
          <h3>Pending join requests</h3>
          <p class="group-description">Review and approve people who want to join your group.</p>
        </div>
        ${rows}
      </section>
    `;
  }

  canManageMember(member) {
    if (!this.isMod()) {
      return false;
    }
    if (member.user_id === this.currentUser?.id) {
      return false;
    }
    if (member.role === 'owner') {
      return false;
    }
    if (!this.isAdmin() && ['admin', 'moderator'].includes(member.role)) {
      return false;
    }
    return true;
  }

  renderMemberActions(member) {
    if (!this.canManageMember(member)) {
      return '';
    }
    const roleSelect = this.isAdmin() && member.role !== 'owner'
      ? `
        <select class="role-select form-control" data-user-id="${member.user_id}" aria-label="Change role">
          ${ASSIGNABLE_ROLES.map((r) => {
    const selected = member.role === r ? 'selected' : '';
    return `<option value="${r}" ${selected}>${r}</option>`;
  }).join('')}
        </select>
      `
      : '';

    const suspendBtn = this.isAdmin() && member.status === 'active'
      ? `<button type="button" class="btn btn-secondary btn-sm suspend-btn" data-user-id="${member.user_id}">Suspend</button>`
      : '';
    const restoreBtn = this.isAdmin() && member.status === 'suspended'
      ? `<button type="button" class="btn btn-secondary btn-sm restore-btn" data-user-id="${member.user_id}">Restore</button>`
      : '';
    const removeBtn = `<button type="button" class="btn btn-secondary btn-sm remove-btn" data-user-id="${member.user_id}">Remove</button>`;

    return `
      <div class="member-actions">
        ${roleSelect}
        ${suspendBtn}
        ${restoreBtn}
        ${removeBtn}
      </div>
    `;
  }

  renderMemberRow(member) {
    const statusNote = member.status !== 'active'
      ? ` · ${member.status}`
      : '';
    return `
      <div class="member-row" data-user-id="${member.user_id}">
        <div class="member-info">
          <span class="member-username">${escapeHtml(member.username)}</span>
          <span class="member-meta">
            <span class="role-badge">${escapeHtml(member.role)}</span>
            Joined ${this.formatDate(member.joined_at)}${escapeHtml(statusNote)}
          </span>
        </div>
        ${this.renderMemberActions(member)}
      </div>
    `;
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading members…</div>
        </div>
      `;
      return;
    }

    if (this.error) {
      this.innerHTML = `<p class="error">${escapeHtml(this.error)}</p>`;
      return;
    }

    const roster = this.members.length
      ? `<div class="member-roster-list">${this.members.map((m) => this.renderMemberRow(m)).join('')}</div>`
      : '<p class="empty-hint">No members yet.</p>';

    const singleMemberNudge = this.members.length === 1 && this.isMod()
      ? '<p class="empty-hint">You\'re the only member — use <strong>Invite</strong> on the group page to grow the roster!</p>'
      : '';

    this.innerHTML = `
      <div class="group-settings-members-content">
        <div class="alert-container"></div>
        ${this.renderInvitePolicySection()}
        ${this.renderJoinRequests()}
        <section class="settings-group">
          <div class="group-header">
            <h3>Members (${this.members.length})</h3>
            <p class="group-description">Everyone in this group and their roles.</p>
          </div>
          ${singleMemberNudge}
          ${roster}
        </section>
      </div>
    `;
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    if (!container) return;
    container.innerHTML = `<div class="alert alert-${type}">${escapeHtml(message)}</div>`;
    setTimeout(() => {
      if (container) container.innerHTML = '';
    }, 4000);
  }

  attachListeners() {
    this.querySelector('#saveInvitePolicyBtn')?.addEventListener('click', () => this.saveInvitePolicy());

    this.querySelectorAll('.approve-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const requestId = btn.getAttribute('data-request-id');
        try {
          await window.API.request(`/groups/${this.groupId}/join-requests/${requestId}/approve`, {
            method: 'POST'
          });
          this.showAlert('Join request approved', 'success');
          await this.load();
          this.dispatchEvent(new CustomEvent('group-members-changed', { bubbles: true }));
        } catch (e) {
          this.showAlert(e.message || 'Could not approve request', 'error');
        }
      });
    });

    this.querySelectorAll('.deny-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const requestId = btn.getAttribute('data-request-id');
        try {
          await window.API.request(`/groups/${this.groupId}/join-requests/${requestId}/deny`, {
            method: 'POST'
          });
          this.showAlert('Join request denied', 'success');
          await this.load();
        } catch (e) {
          this.showAlert(e.message || 'Could not deny request', 'error');
        }
      });
    });

    this.querySelectorAll('.role-select').forEach((sel) => {
      sel.addEventListener('change', async () => {
        const userId = sel.getAttribute('data-user-id');
        const role = sel.value;
        try {
          await window.API.request(`/groups/${this.groupId}/members/${userId}/role`, {
            method: 'PUT',
            body: JSON.stringify({ role })
          });
          this.showAlert('Role updated', 'success');
          await this.load();
          this.dispatchEvent(new CustomEvent('group-members-changed', { bubbles: true }));
        } catch (e) {
          this.showAlert(e.message || 'Could not update role', 'error');
          await this.load();
        }
      });
    });

    this.querySelectorAll('.suspend-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const userId = btn.getAttribute('data-user-id');
        if (!window.confirm('Suspend this member?')) return;
        try {
          await window.API.request(`/groups/${this.groupId}/members/${userId}/role`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'suspended' })
          });
          this.showAlert('Member suspended', 'success');
          await this.load();
        } catch (e) {
          this.showAlert(e.message || 'Could not suspend member', 'error');
        }
      });
    });

    this.querySelectorAll('.restore-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const userId = btn.getAttribute('data-user-id');
        try {
          await window.API.request(`/groups/${this.groupId}/members/${userId}/role`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'active' })
          });
          this.showAlert('Member restored', 'success');
          await this.load();
        } catch (e) {
          this.showAlert(e.message || 'Could not restore member', 'error');
        }
      });
    });

    this.querySelectorAll('.remove-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const userId = btn.getAttribute('data-user-id');
        if (!window.confirm('Remove this member from the group?')) return;
        try {
          await window.API.request(`/groups/${this.groupId}/members/${userId}`, {
            method: 'DELETE'
          });
          this.showAlert('Member removed', 'success');
          await this.load();
          this.dispatchEvent(new CustomEvent('group-members-changed', { bubbles: true }));
        } catch (e) {
          this.showAlert(e.message || 'Could not remove member', 'error');
        }
      });
    });
  }

  async saveInvitePolicy() {
    if (this.savingPolicy || !this.isAdmin()) return;
    const inviteSelect = this.querySelector('#invitePolicySelect');
    const eventSelect = this.querySelector('#eventCreatePolicySelect');
    const rssSelect = this.querySelector('#rssAddPolicySelect');
    if (!inviteSelect || !eventSelect || !rssSelect) return;
    this.savingPolicy = true;
    this.render();
    this.attachListeners();
    try {
      await window.API.request(`/groups/${this.groupId}`, {
        method: 'PUT',
        body: JSON.stringify({
          invite_policy: inviteSelect.value,
          event_create_policy: eventSelect.value,
          rss_add_policy: rssSelect.value
        })
      });
      this.group = await window.API.request(`/groups/${this.groupId}`);
      this.showAlert('Group policies saved', 'success');
      this.dispatchEvent(new CustomEvent('group-settings-saved', {
        detail: { group: this.group },
        bubbles: true
      }));
    } catch (e) {
      this.showAlert(e.message || 'Could not save policies', 'error');
    } finally {
      this.savingPolicy = false;
      this.render();
      this.attachListeners();
    }
  }
}

customElements.define('group-settings-members', GroupSettingsMembers);
export default GroupSettingsMembers;
